import UserService from '@/services/user-service';
import { AuthMeResponse } from '@/utils/auth-me';
import { prefetchMyVariables } from '@/hooks/queries/variables/variables-v2';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthService } from 'Src/services/auth-service';
import { clearCsrfToken } from 'Src/services/utils/axios';
import { useAuthStore } from '../store/use-auth-store';
import { useLoaderStore } from '../store/use-loader-store';
import { useTenantStore } from '../store/use-tenant-store';
import { useUserStore } from '../store/use-user-store';
import {
  clearSoulMapLoginFlow,
  clearPostLogoutLoginPath,
  getAuth0LogoutReturnTo,
  isSoulMapConsumerSession,
  shouldMarkSoulMapPostLogout,
} from '@/utils/soulmap-auth';
import {
  clearBackendSessionValidated,
  markBackendSessionValidated,
  markLoggingOut,
  resetAuthSessionFlags,
} from '../utils/auth-session';
import {
  applyAuthMeToStores,
  clearAuthQueryCache,
  clearEstablishedAuth0Sub,
} from '@/utils/sync-auth-session';
import { AuthMeResponse } from '@/utils/auth-me';
import { clearPostLoginReturnPath } from '@/utils/post-login-return-path';

interface LoginCredentials {
  email: string;
  password: string;
}

export const useAuthQuery = () => {
  const { setSession, reset, setTenants, setShowTenantSelector } =
    useAuthStore();
  const { setUser, clearUser } = useUserStore();
  const setIsLoading = useLoaderStore((state) => state.setIsLoading);
  const { logout: logoutThroughSSO } = useAuth0();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await AuthService.login(credentials);
      return response;
    },
    onSuccess: async (data) => {
      try {
        clearSoulMapLoginFlow();
        clearEstablishedAuth0Sub();
        clearAuthQueryCache(queryClient);

        const tenants = data.tenants || [];
        setTenants(tenants);

        if (data.tenants && data.tenants.length > 1) {
          setShowTenantSelector(true);
          setSession('password');
          if (data.user) {
            setUser(data.user);
          }
        } else if (data.tenants?.[0]) {
          setSession('password');
          const me = (await UserService.getCurrentUser({
            tenantId: data.tenants[0].id,
          })) as AuthMeResponse;
          applyAuthMeToStores(me);
          markBackendSessionValidated();
          await prefetchMyVariables(queryClient, undefined, {
            tenantId: data.tenants[0].id,
          });
          void queryClient.invalidateQueries({ queryKey: ['userPermissions'] });
        } else if (data.user) {
          setUser(data.user);
        }
      } catch (e) {
        console.error('Error in login mutation:', e);
      }
    },
  });

  const logoutMutation = useMutation({
    onMutate: () => {
      markLoggingOut();
      clearPostLoginReturnPath();
    },
    mutationFn: async () => {
      setIsLoading(true);
      await queryClient.cancelQueries();

      await AuthService.logout();
    },
    onSuccess: () => {
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
    onSettled: async () => {
      const authMethod = useAuthStore.getState().authMethod;
      const pathname = window.location.pathname;
      const soulMapSession = isSoulMapConsumerSession();
      // Soul Map SSO logout stays on-page (openUrl: false) — do not stash a post-logout redirect.
      const logoutReturnTo = getAuth0LogoutReturnTo(pathname, {
        markPostLogout:
          shouldMarkSoulMapPostLogout(pathname) &&
          !(authMethod === 'sso' && soulMapSession),
      });

      reset();
      clearUser();
      useTenantStore.getState().reset();
      clearCsrfToken();
      setIsLoading(false);
      resetAuthSessionFlags();
      clearBackendSessionValidated();

      if (authMethod === 'sso') {
        if (soulMapSession) {
          clearSoulMapLoginFlow();
        }

        try {
          await logoutThroughSSO({
            ...(soulMapSession ? { openUrl: false as const } : {}),
            logoutParams: {
              returnTo: logoutReturnTo,
            },
          });
        } catch (error) {
          console.error('Auth0 logout failed:', error);
        }

        if (soulMapSession) {
          clearPostLogoutLoginPath();
          queryClient.clear();
        }
        return;
      }

      clearSoulMapLoginFlow();
      clearPostLogoutLoginPath();

      queryClient.clear();
    },
  });

  return {
    login: loginMutation,
    logout: logoutMutation,
  };
};
